import type { TreatStatusDto } from "@boomap/contracts";

import { TREAT_COLORS, TREAT_DEEP_COLORS } from "../lib/spot-offer.ts";

/**
 * Contour **blanc**, et non sombre.
 *
 * Un trait sombre disparaissait sur le fond de nuit : le bonbon s'y réduisait à
 * une tache orange. Le blanc détache la silhouette sur la carte sombre, et sur
 * la carte claire c'est le corps coloré qui porte le contraste. Un seul jeu
 * d'images sert donc les deux thèmes.
 */
const OUTLINE = "#fff";

/** Trait sombre réservé à la barre du bonbon épuisé. */
const BAR = "#3f4653";

export const ICON_VIEWPORT = 44;

/**
 * Reflet : un arc court, pas une ellipse. La version précédente était si large
 * qu'elle donnait un œil au bonbon ; et l'arc sombre qui figurait la torsade,
 * avant elle, se lisait comme une bouche tombante.
 */
const SHINE = `<path d="M17.4 18.6 Q19.4 15.9 22.5 15.7" fill="none" stroke="${OUTLINE}"
  stroke-width="2.3" stroke-linecap="round" opacity="0.9"/>`;

const wrappedCandy = (treats: TreatStatusDto, extra = ""): string => `
  <g stroke="${OUTLINE}" stroke-width="2.1" stroke-linejoin="round" stroke-linecap="round">
    <path fill="${TREAT_DEEP_COLORS[treats]}" d="M14.4 22 L4.2 13.4 Q7.4 22 4.2 30.6 Z"/>
    <path fill="${TREAT_DEEP_COLORS[treats]}" d="M29.6 22 L39.8 13.4 Q36.6 22 39.8 30.6 Z"/>
    <circle fill="${TREAT_COLORS[treats]}" cx="22" cy="22" r="9.4"/>
  </g>
  ${SHINE}
  ${extra}`;

const SHAPES: Record<TreatStatusDto, () => string> = {
  available: () => wrappedCandy("available"),
  // Barre doublée : liseré blanc dessous, trait sombre dessus, lisible partout.
  exhausted: () =>
    wrappedCandy(
      "exhausted",
      `<path d="M12.6 31.4 L31.4 12.6" stroke="${OUTLINE}" stroke-width="5"
         stroke-linecap="round"/>
       <path d="M12.6 31.4 L31.4 12.6" stroke="${BAR}" stroke-width="2.8"
         stroke-linecap="round"/>`,
    ),
  // Plus saisissable, mais peut subsister en base.
  none: () => `
    <g stroke="${OUTLINE}" stroke-width="2.4" stroke-linejoin="round" stroke-linecap="round">
      <path fill="${TREAT_COLORS.none}" d="M22 8.6c-5.6 0-10.2 4.6-10.2 10.2v13.8c1.7 2 3.4 2 5.1 0s3.4-2 5.1 0 3.4 2 5.1 0 3.4-2 5.1 0V18.8C32.2 13.2 27.6 8.6 22 8.6z"/>
      <circle fill="${BAR}" stroke="none" cx="18.6" cy="19" r="1.9"/>
      <circle fill="${BAR}" stroke="none" cx="25.4" cy="19" r="1.9"/>
    </g>`,
};

export const spotIconSvg = (treats: TreatStatusDto): string =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${ICON_VIEWPORT}" height="${ICON_VIEWPORT}" viewBox="0 0 ${ICON_VIEWPORT} ${ICON_VIEWPORT}">${SHAPES[treats]()}</svg>`;

export const spotIconDataUri = (treats: TreatStatusDto): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(spotIconSvg(treats))}`;
